/**
 * The component wraps the react-modal to apply the basic styles and behaviors we need for all modals.
 * @See http://reactcommunity.org/react-modal/#usage
 */
import cx from 'classnames';
import React, { forwardRef, useEffect } from 'react';
import Modal from 'react-modal';

import styles from './Modal.module.scss';

type ModalWrapperProps = Modal.Props & {
  mediaOnly?: boolean;
  isForm?: boolean;
  isSheet?: boolean;
  isLightbox?: boolean;
  children?: React.ReactNode;
  focusElementId?: string;
};

// Bind the modal to the root appElement (http://reactcommunity.org/react-modal/accessibility/)
// Check if it exists first so unit tests don't try to bind to root
if (document.getElementById('root')) Modal.setAppElement('#root');

const ModalWrapper = forwardRef<Modal, ModalWrapperProps>(
  (
    {
      children,
      className,
      overlayClassName,
      bodyOpenClassName,
      mediaOnly,
      isForm,
      isSheet,
      isLightbox,
      focusElementId,
      ...props
    },
    ref,
  ) => {
    const defaultModalClasses: Modal.Classes = {
      base: cx(styles.modal, {
        [styles.modalMediaOnly]: mediaOnly,
        [styles.modalForm]: isForm,
        [styles.modalSheet]: isSheet,
        [styles.modalLightbox]: isLightbox,
      }),
      afterOpen: '',
      beforeClose: '',
    };

    const defaultOverlayClasses: Modal.Classes = {
      base: cx(styles.modalOverlay, {
        [styles.modalOverlayMediaOnly]: mediaOnly,
        [styles.modalOverlayForm]: isForm,
        [styles.modalOverlaySheet]: isSheet,
      }),
      afterOpen: '',
      beforeClose: '',
    };

    /**
     * Merges the default classes and the incoming classes
     *
     * @param defaultClasses The default classes to use for the modal/overlay
     * @param incomingClasses Referencing the prop value coming in
     */
    const mergeClasses = (
      defaultClasses: Modal.Classes,
      incomingClasses: string | Modal.Classes | undefined,
    ) => {
      let implementedClasses = { ...defaultClasses };
      if (typeof incomingClasses === 'string') {
        implementedClasses.base += ` ${incomingClasses}`;
      } else if (incomingClasses) {
        implementedClasses = {
          base: cx(defaultClasses.base, incomingClasses.base),
          afterOpen: cx(defaultClasses.afterOpen, incomingClasses.afterOpen),
          beforeClose: cx(defaultClasses.beforeClose, incomingClasses.beforeClose),
        };
      }

      return implementedClasses;
    };

    const implementedModalClasses = mergeClasses(defaultModalClasses, className);
    const implementedOverlayClasses = mergeClasses(defaultOverlayClasses, overlayClassName);

    const isModalOpen = props.isOpen;
    useEffect(() => {
      document.body.style.overflow = isModalOpen ? 'hidden' : 'unset';

      return () => {
        document.body.style.overflow = 'unset';
      };
    }, [isModalOpen]);

    return (
      <Modal
        ref={ref}
        className={implementedModalClasses}
        overlayClassName={implementedOverlayClasses}
        bodyOpenClassName={cx(bodyOpenClassName)}
        shouldCloseOnEsc
        shouldFocusAfterRender={!focusElementId}
        onAfterOpen={() => {
          if (focusElementId) {
            document.getElementById(focusElementId)?.focus();
          }
        }}
        aria={{
          modal: true,
        }}
        {...props}
      >
        {children}
      </Modal>
    );
  },
);

export { ModalWrapperProps, ModalWrapper as Modal };
